"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import posthog from "posthog-js";

// Add type declaration for window with sessionStartTime
declare global {
  interface Window {
    sessionStartTime?: Date;
  }
}

export function getPosthogInstance() {
  return posthog;
}

export default function PostHogInit() {
  const pathname = usePathname();

  useEffect(() => {
    const apiKey = process.env.NEXT_PUBLIC_POSTHOG_API_KEY;
    const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";
    
    if (typeof window !== "undefined" && apiKey) {
      try {
        console.log("🔍 PostHogInit: Checking PostHog status...");
        console.log("🔍 PostHogInit: PostHog loaded status:", posthog.__loaded);
        console.log("🔍 PostHogInit: PostHog distinct_id:", posthog.get_distinct_id());
        
        if (!posthog.__loaded) {
          console.log("🚀 PostHogInit: Initializing PostHog for Consumer App with person_profiles: 'always'");
          console.log("🔍 PostHogInit: Current pathname:", pathname);
          
          posthog.init(apiKey, {
            api_host: apiHost,
            capture_pageview: true, // Enable autocapture for page views
            capture_pageleave: true, // Enable PostHog's built-in page leave tracking
            autocapture: true, // Automatically capture clicks, form submissions, etc.
            person_profiles: 'always', // Always create person profiles
            persistence: 'localStorage+cookie', // Use both localStorage and cookie for better cross-domain tracking
            debug: false, // Disable debug mode to reduce console noise and allow our logs to show
            enable_recording_console_log: false, // Disable console log recording
            session_recording: {
              // Configure session recording to be more efficient
              maskAllInputs: false, // Show input values in recordings
              maskTextSelector: '.private-info' // Only mask elements with this class
            },
            loaded: function(posthog) {
              console.log("✅ PostHogInit: PostHog loaded successfully with distinct_id:", posthog.get_distinct_id());
              
              // Create a person profile for all users immediately
              try {
                posthog.createPersonProfile();
                console.log("✅ PostHogInit: Person profile created with ID:", posthog.get_distinct_id());
                
                // Start tracking session time
                window.sessionStartTime = new Date();
                
                // Track session start
                posthog.capture('session_started', {
                  timestamp: window.sessionStartTime.toISOString()
                });
              } catch (e) {
                console.error("❌ PostHogInit: Error creating person profile:", e);
              }
            }
          });
          posthog.__loaded = true;
        } else {
          console.log("✅ PostHogInit: PostHog already loaded, ensuring person profile exists");
          
          // PostHog is already loaded, ensure person profile exists
          try {
            if (!posthog.get_distinct_id()) {
              posthog.createPersonProfile();
              console.log("✅ PostHogInit: Person profile created for already-loaded PostHog with ID:", posthog.get_distinct_id());
            } else {
              console.log("✅ PostHogInit: Person profile already exists with ID:", posthog.get_distinct_id());
            }
            
            // Start tracking session time if not already set
            if (!window.sessionStartTime) {
              window.sessionStartTime = new Date();
              console.log("✅ PostHogInit: Session start time set for existing PostHog");
            }
          } catch (e) {
            console.error("❌ PostHogInit: Error ensuring person profile exists:", e);
          }
        }
        
        // Add session duration tracking with multiple fallback methods
        const capturePageLeave = () => {
          console.log('capturePageLeave function called');
          
          if (posthog.__loaded && window.sessionStartTime) {
            const sessionDuration = (new Date().getTime() - window.sessionStartTime.getTime()) / 1000;
            
            console.log('Capturing page leave, session duration:', sessionDuration, 'seconds');
            
            try {
              // Capture $pageleave event - this is what PostHog Web Analytics expects
              posthog.capture('$pageleave', {
                $current_url: window.location.href,
                pathname: window.location.pathname,
                session_duration_seconds: sessionDuration
              });
              
              // Also capture session_ended for our own tracking
              posthog.capture('session_ended', {
                session_duration_seconds: sessionDuration,
                session_duration_readable: formatDuration(sessionDuration)
              });
              
              // Set person properties
              posthog.people.set({
                last_session_duration_seconds: sessionDuration,
                last_session_duration_readable: formatDuration(sessionDuration)
              });
              
              // Try to force immediate send using sendBeacon
              if (navigator.sendBeacon) {
                const apiKey = process.env.NEXT_PUBLIC_POSTHOG_API_KEY;
                const apiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";
                const beaconUrl = `${apiHost}/e/?ip=1&_=1&v=2&api_key=${apiKey}`;
                
                const beaconData = JSON.stringify({
                  batch: [{
                    event: '$pageleave',
                    properties: {
                      $current_url: window.location.href,
                      pathname: window.location.pathname,
                      session_duration_seconds: sessionDuration,
                      distinct_id: posthog.get_distinct_id()
                    }
                  }, {
                    event: 'session_ended',
                    properties: {
                      session_duration_seconds: sessionDuration,
                      session_duration_readable: formatDuration(sessionDuration),
                      distinct_id: posthog.get_distinct_id()
                    }
                  }]
                });
                
                navigator.sendBeacon(beaconUrl, beaconData);
                console.log('Sent page leave events via sendBeacon');
              }
              
              console.log('Page leave events captured successfully');
              
            } catch (error) {
              console.error('Error capturing page leave:', error);
            }
          } else {
            console.log('PostHog not loaded or sessionStartTime not set');
            console.log('posthog.__loaded:', posthog.__loaded);
            console.log('window.sessionStartTime:', window.sessionStartTime);
          }
        };

        // Method 1: beforeunload (most reliable for actual browser close)
        window.addEventListener('beforeunload', () => {
          console.log('Beforeunload event fired');
          capturePageLeave();
        });
        console.log('Beforeunload event listener attached');

        // Method 2: pagehide (more reliable for navigation)
        window.addEventListener('pagehide', (event) => {
          console.log('Pagehide event fired, persisted:', event.persisted);
          // Capture page leave regardless of persisted state for better coverage
          capturePageLeave();
        });
        console.log('Pagehide event listener attached');

        // Method 3: unload (most reliable for page exit)
        window.addEventListener('unload', () => {
          console.log('Unload event fired');
          capturePageLeave();
        });
        console.log('Unload event listener attached');

        // Method 4: visibilitychange (for when user switches tabs/apps)
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden') {
            console.log('Page became hidden');
            // Only capture if user has been on page for more than 5 seconds
            if (posthog.__loaded && window.sessionStartTime) {
              const sessionDuration = (new Date().getTime() - window.sessionStartTime.getTime()) / 1000;
              if (sessionDuration > 5) {
                capturePageLeave();
              }
            }
          }
        });

        // Method 5: Periodic heartbeat to track page activity
        let lastActivity = Date.now();
        
        const updateActivity = () => {
          lastActivity = Date.now();
        };
        
        // Track user activity
        ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'].forEach(event => {
          document.addEventListener(event, updateActivity, true);
        });
        
        // Check for page inactivity every 10 seconds (more frequent)
        const pageActivityInterval = setInterval(() => {
          const now = Date.now();
          const timeSinceActivity = now - lastActivity;
          
          // If no activity for 1 minute, consider it a "page leave" (more aggressive)
          if (timeSinceActivity > 60000 && posthog.__loaded && window.sessionStartTime) {
            console.log('Page inactivity detected, capturing page leave');
            capturePageLeave();
            clearInterval(pageActivityInterval);
          }
        }, 10000);

        // Method 6: Periodic session duration updates (every 10 seconds)
        const sessionUpdateInterval = setInterval(() => {
          if (posthog.__loaded && window.sessionStartTime) {
            const sessionDuration = (new Date().getTime() - window.sessionStartTime.getTime()) / 1000;
            
            // Update person properties with current session duration
            posthog.people.set({
              current_session_duration_seconds: sessionDuration,
              current_session_duration_readable: formatDuration(sessionDuration)
            });
            
            console.log('Updated session duration:', sessionDuration, 'seconds');
          }
        }, 10000);
        
      } catch (error) {
        console.error("Error initializing PostHog:", error);
      }
    }

    // Register UTM params on first load
    if (typeof window !== "undefined" && posthog.__loaded) {
      const params = new URLSearchParams(window.location.search);
      const utm_source = params.get("utm_source");
      const utm_medium = params.get("utm_medium");
      const utm_campaign = params.get("utm_campaign");
      
      // Capture additional URL parameters if they exist
      const landingUrl = window.location.href;
      const referrer = document.referrer;
      
      // Register properties with PostHog
      posthog.register({
        utm_source,
        utm_medium,
        utm_campaign,
        landing_url: landingUrl,
        referrer: referrer || undefined,
        initial_pathname: pathname,
      });
    }
  }, [pathname]);

  // Track pageview on route change
  useEffect(() => {
    if (posthog.__loaded) {
      posthog.capture('$pageview', {
        $current_url: window.location.href,
        pathname: pathname,
      });
    }
  }, [pathname]);

  // Scroll depth tracking
  useEffect(() => {
    if (typeof window === "undefined" || !posthog.__loaded) return;

    // Track which scroll depth thresholds have been reached for this page view
    const reachedThresholds = new Set<number>();
    
    // Scroll depth thresholds (20%, 40%, 60%, 80%)
    const scrollThresholds = [20, 40, 60, 80];
    
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;
      
      // Calculate current scroll percentage
      const scrollPercentage = Math.round((scrollTop / (scrollHeight - clientHeight)) * 100);
      
      // Check each threshold
      scrollThresholds.forEach(threshold => {
        if (scrollPercentage >= threshold && !reachedThresholds.has(threshold)) {
          // Mark this threshold as reached
          reachedThresholds.add(threshold);
          
          // Send scroll depth event to PostHog
          posthog.capture('scrolled_to_depth', {
            scroll_depth_percent: threshold,
            current_scroll_percent: scrollPercentage,
            pathname: pathname,
            $current_url: window.location.href
          });
          
          console.log(`🎯 PostHogInit: Scroll depth ${threshold}% reached on ${pathname}`);
        }
      });
    };

    // Add scroll event listener
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    // Cleanup function to remove event listener
    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Clear reached thresholds for next page
      reachedThresholds.clear();
    };
  }, [pathname]);

  return null;
}

// Helper function to format duration in seconds to a readable format
function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${Math.round(seconds)} seconds`;
  } else if (seconds < 3600) {
    return `${Math.floor(seconds / 60)} minutes ${Math.round(seconds % 60)} seconds`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours} hours ${minutes} minutes`;
  }
}
